import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const ROOT_MARGIN = "400px";

const rowId = (row) => row?.id ?? row?.appointment_id;

// A value-keyed identity for the query, so a parent that rebuilds the same
// object every render does not refetch, and a real change (any key, any
// depth) does. Keys are sorted so the order they were written in is moot.
const stableKey = (value) =>
  JSON.stringify(value, (_, v) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.keys(v).sort().reduce((acc, k) => { acc[k] = v[k]; return acc; }, {})
      : v
  );

/**
 * A cursor-paged list that loads more as the user scrolls.
 *
 * `fetchPage({...query, cursor, limit})` resolves to
 * `{items, nextCursor, hasMore, total?, counts?}`. Page 1 is fetched on
 * mount and whenever `query` changes by value; `loadMore` appends the next
 * page, de-duplicated by id. Every request is stamped: a page that resolves
 * after a newer request started belongs to a list the user has left and is
 * dropped. `total` and `counts` arrive with page 1 only and are kept while
 * later pages append.
 *
 * Give the scrolling element to `scrollRootRef` and the last row to
 * `sentinelRef` (a callback ref): an IntersectionObserver rooted at the box
 * with a 400px margin calls `loadMore` as the sentinel comes near. Where
 * there is no IntersectionObserver (jsdom), nothing observes and `loadMore`
 * is the only way on.
 */
export function useInfiniteList({ fetchPage, query, pageSize = 100, enabled = true }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(null);
  const [counts, setCounts] = useState(null);
  const [sentinel, setSentinel] = useState(null);

  const scrollRootRef = useRef(null);
  const reqRef = useRef(0);
  const cursorRef = useRef(null);
  const hasMoreRef = useRef(false);
  const busyRef = useRef(false);
  const queryRef = useRef(query);
  queryRef.current = query;
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  const queryKey = useMemo(() => stableKey(query ?? {}), [query]);

  const loadFirst = useCallback(async () => {
    const reqId = ++reqRef.current;
    busyRef.current = true;
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    try {
      const page = await fetchRef.current({ ...queryRef.current, cursor: null, limit: pageSize });
      if (reqRef.current !== reqId) return;
      cursorRef.current = page.nextCursor ?? null;
      hasMoreRef.current = Boolean(page.hasMore);
      setRows(Array.isArray(page.items) ? page.items : []);
      setHasMore(hasMoreRef.current);
      setTotal(typeof page.total === "number" ? page.total : null);
      setCounts(page.counts ?? null);
    } catch (e) {
      if (reqRef.current !== reqId) return;
      setError(e?.message || "Failed to load");
    } finally {
      if (reqRef.current === reqId) {
        busyRef.current = false;
        setLoading(false);
      }
    }
  }, [pageSize]);

  const loadMore = useCallback(async () => {
    if (!hasMoreRef.current || busyRef.current) return;
    const reqId = ++reqRef.current;
    busyRef.current = true;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await fetchRef.current({ ...queryRef.current, cursor: cursorRef.current, limit: pageSize });
      if (reqRef.current !== reqId) return;
      cursorRef.current = page.nextCursor ?? null;
      hasMoreRef.current = Boolean(page.hasMore);
      setRows((prev) => {
        const seen = new Set(prev.map(rowId));
        const fresh = (page.items || []).filter((r) => !seen.has(rowId(r)));
        return fresh.length ? [...prev, ...fresh] : prev;
      });
      setHasMore(hasMoreRef.current);
      if (typeof page.total === "number") setTotal(page.total);
      if (page.counts) setCounts(page.counts);
    } catch (e) {
      if (reqRef.current !== reqId) return;
      setError(e?.message || "Failed to load more");
    } finally {
      if (reqRef.current === reqId) {
        busyRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [pageSize]);

  const reset = useCallback(() => {
    reqRef.current += 1;
    busyRef.current = false;
    cursorRef.current = null;
    hasMoreRef.current = false;
    setRows([]);
    setHasMore(false);
    setTotal(null);
    setCounts(null);
    setError(null);
    setLoadingMore(false);
    return loadFirst();
  }, [loadFirst]);

  // Page 1 for every new query, and a clean slate when the list is switched off.
  useEffect(() => {
    if (!enabled) {
      reqRef.current += 1;
      busyRef.current = false;
      cursorRef.current = null;
      hasMoreRef.current = false;
      setRows([]);
      setHasMore(false);
      setTotal(null);
      setCounts(null);
      setError(null);
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    reset();
  }, [queryKey, enabled, reset]);

  // One observer per (sentinel, list state): re-made after every page lands
  // so a sentinel that is still in view keeps loading until it is not.
  useEffect(() => {
    if (!sentinel || !enabled || !hasMore || loading || loadingMore) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) loadMore(); },
      { root: scrollRootRef.current, rootMargin: ROOT_MARGIN }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [sentinel, enabled, hasMore, loading, loadingMore, loadMore]);

  return {
    rows, setRows, loading, loadingMore, hasMore, error, total, counts,
    loadMore, reset, refresh: loadFirst, sentinelRef: setSentinel, scrollRootRef,
  };
}
